import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { MapPin, Clock, Phone, Mail } from 'lucide-react';

export const InteractiveBranding: React.FC = () => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [activeLocation, setActiveLocation] = useState<'aurora' | 'denver'>('aurora');

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Entrance animation for content
    useEffect(() => {
        if (contentRef.current) {
            gsap.fromTo(
                contentRef.current.children,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    stagger: 0.15,
                    ease: 'power3.out',
                    delay: 0.3
                }
            );
        }
    }, []);

    if (isMobile) {
        return (
            <div className="relative w-full bg-white flex flex-col items-center justify-center p-8 py-16">
                <div className="w-full max-w-md">

                    <div className="space-y-6 text-left">
                        <div className="space-y-2">
                            <p className="text-neutral-700 flex items-center gap-2">
                                <Phone size={14} className="text-mscg-orange" />
                                <a href="tel:303-323-8730" className="hover:text-mscg-orange transition-colors">303-323-8730</a>
                            </p>
                            <p className="text-neutral-700 flex items-center gap-2">
                                <Mail size={14} className="text-mscg-orange" />
                                <a href="mailto:info@mrscounseling.com" className="hover:text-mscg-orange transition-colors">info@mrscounseling.com</a>
                            </p>
                        </div>

                        {/* Location Toggle */}
                        <div>
                            <h4 className="font-bold text-mscg-dark-gray mb-3 uppercase tracking-wider text-sm flex items-center gap-2">
                                <MapPin size={14} />
                                Our Locations
                            </h4>
                            <div className="flex rounded-full bg-neutral-100 p-1 mb-3">
                                <button
                                    onClick={() => setActiveLocation('aurora')}
                                    className={`flex-1 py-2 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeLocation === 'aurora' ? 'bg-[#d68800] text-white shadow-md' : 'text-neutral-600 hover:text-neutral-900'}`}
                                >
                                    Aurora Office
                                </button>
                                <button
                                    onClick={() => setActiveLocation('denver')}
                                    className={`flex-1 py-2 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeLocation === 'denver' ? 'bg-[#d68800] text-white shadow-md' : 'text-neutral-600 hover:text-neutral-900'}`}
                                >
                                    Denver Office
                                </button>
                            </div>
                            <div className="rounded-lg overflow-hidden shadow-sm border border-neutral-200">
                                {activeLocation === 'aurora' ? (
                                    <iframe
                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3073.5!2d-104.859422!3d39.673865!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDQwJzI2LjAiTiAxMDTCsDUxJzM0LjAiVw!5e0!3m2!1sen!2sus!4v1"
                                        width="100%" height="180" style={{ border: 0 }} allowFullScreen loading="lazy"
                                    />
                                ) : (
                                    <iframe
                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3066.5!2d-104.850366!3d39.781988!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876c7b33ee33ef75%3A0x60b5a84d56bbda97!2s12000%20E%2047th%20Ave%20%23113%2C%20Denver%2C%20CO%2080239!5e0!3m2!1sen!2sus!4v1"
                                        width="100%" height="180" style={{ border: 0 }} allowFullScreen loading="lazy"
                                    />
                                )}
                            </div>
                            <p className="text-xs text-neutral-500 mt-2 text-center">
                                {activeLocation === 'aurora' ? '12510 E. Iliff Ave. Suite 230, Aurora' : '12000 E 47th Ave #113, Denver'}
                            </p>
                        </div>

                        {/* Working Hours */}
                        <div className="flex items-center gap-2 text-neutral-600 text-sm">
                            <Clock size={14} />
                            <span>Mon–Fri 8am–6pm | By Appointment Only</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative w-full h-full min-h-screen bg-white flex items-center justify-center p-12 pt-20">
            <div
                ref={contentRef}
                className="w-full max-w-lg"
            >

                <div className="space-y-8">
                    <div className="space-y-3">
                        <p className="text-neutral-700 text-lg leading-relaxed flex items-center gap-3">
                            <Phone size={18} className="text-mscg-orange" />
                            <a href="tel:303-323-8730" className="hover:text-mscg-orange transition-colors">303-323-8730</a>
                        </p>
                        <p className="text-neutral-700 text-lg leading-relaxed flex items-center gap-3">
                            <Mail size={18} className="text-mscg-orange" />
                            <a href="mailto:info@mrscounseling.com" className="hover:text-mscg-orange transition-colors">info@mrscounseling.com</a>
                        </p>
                    </div>

                    {/* Location Toggle */}
                    <div>
                        <h4 className="font-bold text-mscg-dark-gray mb-3 uppercase tracking-wider text-sm flex items-center gap-2">
                            <MapPin size={14} />
                            Our Locations
                        </h4>
                        <div className="flex rounded-full bg-neutral-100 p-1 mb-4 shadow-inner">
                            <button
                                onClick={() => setActiveLocation('aurora')}
                                className={`flex-1 py-2.5 px-5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeLocation === 'aurora' ? 'bg-[#d68800] text-white shadow-md' : 'text-neutral-600 hover:text-neutral-900'}`}
                            >
                                Aurora Office
                            </button>
                            <button
                                onClick={() => setActiveLocation('denver')}
                                className={`flex-1 py-2.5 px-5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeLocation === 'denver' ? 'bg-[#d68800] text-white shadow-md' : 'text-neutral-600 hover:text-neutral-900'}`}
                            >
                                Denver Office
                            </button>
                        </div>
                        <div className="rounded-lg overflow-hidden shadow-md border border-neutral-200 transition-opacity duration-300">
                            {activeLocation === 'aurora' ? (
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3073.5!2d-104.859422!3d39.673865!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDQwJzI2LjAiTiAxMDTCsDUxJzM0LjAiVw!5e0!3m2!1sen!2sus!4v1"
                                    width="100%" height="220" style={{ border: 0 }} allowFullScreen loading="lazy"
                                />
                            ) : (
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3066.5!2d-104.850366!3d39.781988!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876c7b33ee33ef75%3A0x60b5a84d56bbda97!2s12000%20E%2047th%20Ave%20%23113%2C%20Denver%2C%20CO%2080239!5e0!3m2!1sen!2sus!4v1"
                                    width="100%" height="220" style={{ border: 0 }} allowFullScreen loading="lazy"
                                />
                            )}
                        </div>
                        <p className="text-sm text-neutral-500 mt-3">
                            {activeLocation === 'aurora' ? '12510 E. Iliff Ave. Suite 230, Aurora, CO 80014' : '12000 E 47th Ave #113, Denver, CO 80239'}
                        </p>
                    </div>

                    {/* Working Hours */}
                    <div className="flex items-center gap-2 text-lg text-neutral-600">
                        <Clock size={16} />
                        <span>Monday–Friday 8:00am–6:00pm | By Appointment Only</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
